import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { FiCheck, FiCopy } from "react-icons/fi";

const toCents = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
};

const selectedReasons = (summary) => (summary?.Tab1?.howDidThisHappen || [])
  .map((reason) => String(reason || "").trim())
  .filter(Boolean);

export function buildStudentFollowUpMessage(summary) {
  const budget = summary?.Tab1?.monthlyHouseholdIncome;
  if (!budget) return null;

  const money = (cents) => (cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const income = toCents(budget.Netincome);
  const expenses = toCents(budget.Householdexpenses) + toCents(budget.debtPayments);
  const difference = income - expenses;
  const reasons = selectedReasons(summary).join(", ");
  const introduction = reasons
    ? `I see you shared that what brought you here was ${reasons}. Remember that you are not alone in this. We hope the course gave you the necessary tools to help you move toward the fresh start you deserve.`
    : "";
  const budgetParagraph = `Let’s take a quick look at your monthly budget. Your income is $${money(income)} and your expenses are $${money(expenses)}. That leaves a difference of $${money(difference)}. Is that correct?`;

  return introduction ? `${introduction}\n\n${budgetParagraph}` : budgetParagraph;
}

export async function copyText(text) {
  if (window.isSecureContext !== false && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Clipboard permission can still be denied on HTTPS. Keep the fallback
      // inside the original click handler so it also works on HTTP dev servers.
    }
  }

  const helper = document.createElement("textarea");
  helper.value = text;
  helper.setAttribute("readonly", "");
  helper.style.position = "fixed";
  helper.style.top = "-9999px";
  helper.style.opacity = "0";
  document.body.appendChild(helper);
  helper.focus();
  helper.select();
  helper.setSelectionRange(0, helper.value.length);
  let copied = false;
  try {
    copied = document.execCommand("copy");
  } finally {
    document.body.removeChild(helper);
  }
  return copied;
}

function EditableTemplate({ message }) {
  const [text, setText] = useState(message);
  const [copied, setCopied] = useState(false);
  const field = useRef(null);
  const copiedTimer = useRef(null);

  useEffect(() => () => clearTimeout(copiedTimer.current), []);
  useLayoutEffect(() => {
    const resize = () => {
      const textarea = field.current;
      if (!textarea) return;
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [text]);

  const copy = async () => {
    try {
      if (!await copyText(text)) return;
      clearTimeout(copiedTimer.current);
      setCopied(true);
      copiedTimer.current = setTimeout(() => setCopied(false), 1400);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="budget-template-card" aria-label="Student follow-up template">
      <button
        type="button"
        className={`budget-template-copy${copied ? " is-copied" : ""}`}
        onClick={copy}
        aria-label={copied ? "Template copied" : "Copy template"}
        title={copied ? "Copied" : "Copy template"}
      >
        {copied ? <FiCheck size={21} aria-hidden="true" /> : <FiCopy size={20} aria-hidden="true" />}
      </button>
      <textarea
        ref={field}
        className="budget-template-text"
        value={text}
        aria-label="Edit follow-up template"
        onChange={(event) => { setText(event.target.value); setCopied(false); }}
      />
      <span role="status" className="visually-hidden">{copied ? "Copied" : ""}</span>
    </section>
  );
}

export default function StudentFollowUpMessage({ summaryData }) {
  const message = buildStudentFollowUpMessage(summaryData);
  if (!message) return null;

  return (
    <section className="budget-templates" aria-labelledby="budget-templates-title">
      <h4 id="budget-templates-title" className="card-title mb-4">Templates</h4>
      <EditableTemplate key={message} message={message} />
    </section>
  );
}
